import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetSourcingEventQuery,
  useStartSeV2ClarificationMutation,
} from "../../../../autogen/bff-api";
import { AttachmentPicker } from "../../../../common/components/communication/AttachmentPicker";
import { richTextHasContent } from "../../../../common/components/communication/communicationUtils";
import { useMessageAttachments } from "../../../../common/components/communication/useMessageAttachments";
import { useApiError } from "../../../../common/errors/useApiError";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { citesFoaProvisions } from "../../../sourcing-events/buyer/wizard/utils/publicProcurementOptions";

export interface ClarificationCounterparty {
  organizationId: string;
  organizationName: string;
}

export const StartClarificationModal = ({
  eventId,
  counterparties,
  fixedCounterpartyId,
  isOpen,
  onClose,
}: {
  eventId: string;
  counterparties: ClarificationCounterparty[];
  fixedCounterpartyId?: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const [start, { isLoading }] = useStartSeV2ClarificationMutation();
  const { data: event } = useGetSourcingEventQuery({ eventId });
  const requestDocuments = (event?.eventData.documents ?? []).map((d) => ({ id: d.id, name: d.name }));

  const [supplierOrganizationId, setSupplierOrganizationId] = useState(
    fixedCounterpartyId ?? counterparties[0]?.organizationId ?? "",
  );
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [resetKey, setResetKey] = useState(0);
  // The answer upload is question-scoped and there is no thread yet; the announcement upload is the buyer's
  // event-scoped one, and it authorizes exactly what is needed here (the buyer, on this event). The file only
  // becomes a clarification attachment when the dialog is created with it.
  const attachments = useMessageAttachments({
    eventId,
    uploadEntityType: "SeV2AnnouncementAttachment",
    pollRole: "buyer",
  });

  const canSend =
    supplierOrganizationId.length > 0 &&
    subject.trim().length > 0 &&
    richTextHasContent(content) &&
    !attachments.isUploading &&
    !attachments.hasFailedUploads;

  const handleSend = async () => {
    if (!canSend) return;
    try {
      await start({
        eventId,
        createSeV2ClarificationRequest: {
          supplierOrganizationId,
          subject: subject.trim(),
          content,
          attachments: attachments.toInputs(),
        },
      }).unwrap();
      setSubject("");
      setContent("");
      attachments.reset();
      setResetKey((key) => key + 1);
      onClose();
    } catch (error) {
      displayer.trigger(error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Start a clarification dialog")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Alert status="info" rounded="md" fontSize="sm">
              <AlertIcon />
              {event?.eventContext?.type === "Public" && citesFoaProvisions(event.eventContext.regulation)
                ? t(
                    "A clarification cannot change the tender — it may only clarify what has already been submitted (FOA § 23-5).",
                  )
                : t("A clarification cannot change the tender — it may only clarify what has already been submitted.")}
            </Alert>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Tenderer")}</FormLabel>
              <Select
                bg="smBackground"
                value={supplierOrganizationId}
                isDisabled={!!fixedCounterpartyId}
                onChange={(e) => setSupplierOrganizationId(e.target.value)}
              >
                {counterparties.map((counterparty) => (
                  <option key={counterparty.organizationId} value={counterparty.organizationId}>
                    {counterparty.organizationName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Subject")}</FormLabel>
              <Input bg="smBackground" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Message")}</FormLabel>
              <TipTap
                key={resetKey}
                content=""
                editable
                debounceWait={0}
                onUpdate={(value) => setContent(value)}
              />
            </FormControl>
            <AttachmentPicker controls={attachments} requestDocuments={requestDocuments} />
          </Stack>
        </ModalBody>
        <ModalFooter justifyContent="flex-start" gap="3">
          <Button colorScheme="teal" onClick={handleSend} isLoading={isLoading} isDisabled={!canSend}>
            {t("Send message")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
